// Grades - three calculators students reach for: a weighted-average grade (by
// category), a "what do I need on the final" solver, and a GPA calculator
// (4.0 scale, with optional honors/AP weighting). Nothing is stored; it's a
// live calculator, so each recomputes as you type.
import { el, btn, input, wrap, sel, field, note } from "./ui.js";

const num = (v) => { const n = parseFloat(v); return isFinite(n) ? n : null; };
const fmt = (n, d) => (Math.round(n * Math.pow(10, d || 2)) / Math.pow(10, d || 2)).toString();

// Letter grade for a percentage (standard US scale).
function letter(pct) {
	if (pct >= 97) return "A+"; if (pct >= 93) return "A"; if (pct >= 90) return "A-";
	if (pct >= 87) return "B+"; if (pct >= 83) return "B"; if (pct >= 80) return "B-";
	if (pct >= 77) return "C+"; if (pct >= 73) return "C"; if (pct >= 70) return "C-";
	if (pct >= 67) return "D+"; if (pct >= 63) return "D"; if (pct >= 60) return "D-";
	return "F";
}

// Results update as you type and are read out politely once typing pauses.
function liveResult() {
	const r = el("div", "rv-grade-result");
	r.setAttribute("aria-live", "polite");
	r.setAttribute("aria-atomic", "true");
	return r;
}

export function grade(root) {
	const s = wrap(root); s.classList.add("rv-grade");
	const tabs = el("div", "rv-grade-tabs");
	const panel = el("div", "rv-grade-panel");
	const modes = [["avg", "Weighted average"], ["final", "Final grade"], ["gpa", "GPA"]];
	let mode = "avg";
	function renderTabs() {
		tabs.textContent = "";
		tabs.setAttribute("role", "group");
		tabs.setAttribute("aria-label", "Calculator");
		modes.forEach(([m, label]) => tabs.appendChild(btn(label, () => { mode = m; renderTabs(); renderPanel(); const b = tabs.querySelector(".active"); if (b) b.focus(); }, "rv-chip" + (mode === m ? " active" : ""))));
	}
	function renderPanel() { panel.textContent = ""; (mode === "avg" ? avg : mode === "final" ? final : gpa)(panel); }
	renderTabs(); renderPanel();
	s.append(tabs, panel);
}

// --- weighted average by category ------------------------------------------
function avg(panel) {
	let rows = [{ name: "Homework", weight: "20", score: "" }, { name: "Tests", weight: "50", score: "" }, { name: "Final", weight: "30", score: "" }];
	const table = el("div", "rv-grade-rows");
	const result = liveResult();

	function compute() {
		let wsum = 0, acc = 0;
		rows.forEach((r) => { const w = num(r.weight), sc = num(r.score); if (w != null && sc != null) { wsum += w; acc += w * sc; } });
		result.textContent = "";
		if (wsum <= 0) { result.append(el("span", "rv-tnote", "Enter category weights and your scores.")); return; }
		const overall = acc / wsum;
		result.append(el("div", "rv-grade-big", fmt(overall) + "%"),
			el("div", "rv-grade-letter", letter(overall)),
			el("div", "rv-tnote", "Based on " + fmt(wsum, 0) + "% of the grade filled in" + (Math.abs(wsum - 100) > 0.01 ? " (weights total " + fmt(wsum, 0) + "%, not 100%)" : "") + "."));
	}
	function draw() {
		table.textContent = "";
		const head = el("div", "rv-grade-row rv-grade-head");
		head.append(el("span", null, "Category"), el("span", null, "Weight %"), el("span", null, "Score %"), el("span", null, ""));
		table.appendChild(head);
		rows.forEach((r, i) => {
			const rowEl = el("div", "rv-grade-row");
			const name = input("text", { value: r.name, placeholder: "Category" });
			const w = input("number", { value: r.weight, placeholder: "0" });
			const sc = input("number", { value: r.score, placeholder: "0" });
			const rowName = () => r.name || "row " + (i + 1);
			name.setAttribute("aria-label", "Category name, row " + (i + 1));
			w.setAttribute("aria-label", "Weight percent, " + rowName());
			sc.setAttribute("aria-label", "Score percent, " + rowName());
			name.addEventListener("input", () => (r.name = name.value));
			w.addEventListener("input", () => { r.weight = w.value; compute(); });
			sc.addEventListener("input", () => { r.score = sc.value; compute(); });
			const rm = btn("✕", () => { rows.splice(i, 1); draw(); compute(); }, "rv-fc-card-del");
			rm.setAttribute("aria-label", "Remove " + rowName());
			rowEl.append(name, w, sc, rm);
			table.appendChild(rowEl);
		});
		compute();
	}
	panel.append(table, btn("+ Add category", () => { rows.push({ name: "", weight: "", score: "" }); draw(); }, "rv-tbtn"), result);
	draw();
}

// --- what do I need on the final -------------------------------------------
function final(panel) {
	const cur = input("number", { placeholder: "e.g. 88" });
	const want = input("number", { placeholder: "e.g. 90" });
	const weight = input("number", { placeholder: "e.g. 20" });
	const result = liveResult();
	function compute() {
		const c = num(cur.value), t = num(want.value), w = num(weight.value);
		result.textContent = "";
		if (c == null || t == null || w == null || w <= 0 || w > 100) { result.append(el("span", "rv-tnote", "Enter your current grade, target grade, and the final's weight.")); return; }
		const wf = w / 100;
		const needed = (t - c * (1 - wf)) / wf;
		const big = el("div", "rv-grade-big", fmt(needed) + "%");
		result.append(big, el("div", "rv-grade-letter", letter(needed)));
		if (needed <= 0) result.append(el("div", "rv-tnote", "You've already secured your target - even a 0% keeps you at or above it."));
		else if (needed > 100) result.append(el("div", "rv-tnote", "That target isn't reachable with this final alone (needs over 100%)."));
		else result.append(el("div", "rv-tnote", "You need " + fmt(needed) + "% on the final to finish with a " + fmt(t) + "%."));
	}
	[cur, want, weight].forEach((i) => i.addEventListener("input", compute));
	panel.append(field("Current grade (%)", cur), field("Target grade (%)", want), field("Final exam weight (% of grade)", weight), result);
	compute();
}

// --- GPA --------------------------------------------------------------------
const GPA_POINTS = { "A+": 4.0, A: 4.0, "A-": 3.7, "B+": 3.3, B: 3.0, "B-": 2.7, "C+": 2.3, C: 2.0, "C-": 1.7, "D+": 1.3, D: 1.0, "D-": 0.7, F: 0.0 };
const LETTERS = Object.keys(GPA_POINTS);
function gpa(panel) {
	let rows = [{ name: "", grade: "A", credits: "1", type: "reg" }, { name: "", grade: "B", credits: "1", type: "reg" }];
	const table = el("div", "rv-grade-rows");
	const result = liveResult();
	function compute() {
		let cr = 0, pts = 0;
		rows.forEach((r) => {
			const c = num(r.credits); if (c == null || c <= 0) return;
			let p = GPA_POINTS[r.grade]; if (p == null) return;
			if (r.type === "honors" && p > 0) p += 0.5; else if (r.type === "ap" && p > 0) p += 1;
			cr += c; pts += p * c;
		});
		result.textContent = "";
		if (cr <= 0) { result.append(el("span", "rv-tnote", "Add courses with a grade and credit hours.")); return; }
		result.append(el("div", "rv-grade-big", fmt(pts / cr)), el("div", "rv-tnote", fmt(cr, 0) + " credit" + (cr === 1 ? "" : "s") + " · weighted for honors/AP where selected."));
	}
	function draw() {
		table.textContent = "";
		const head = el("div", "rv-grade-row rv-grade-gpa-row rv-grade-head");
		head.append(el("span", null, "Course"), el("span", null, "Grade"), el("span", null, "Credits"), el("span", null, "Level"), el("span", null, ""));
		table.appendChild(head);
		rows.forEach((r, i) => {
			const rowEl = el("div", "rv-grade-row rv-grade-gpa-row");
			const name = input("text", { value: r.name, placeholder: "Course" });
			const g = sel(LETTERS.map((l) => [l, l]), r.grade);
			const c = input("number", { value: r.credits, placeholder: "1" });
			const t = sel([["reg", "Regular"], ["honors", "Honors +0.5"], ["ap", "AP/IB +1"]], r.type);
			const course = r.name || "course " + (i + 1);
			name.setAttribute("aria-label", "Course name, row " + (i + 1));
			g.setAttribute("aria-label", "Grade, " + course);
			c.setAttribute("aria-label", "Credits, " + course);
			t.setAttribute("aria-label", "Level, " + course);
			name.addEventListener("input", () => (r.name = name.value));
			g.addEventListener("change", () => { r.grade = g.value; compute(); });
			c.addEventListener("input", () => { r.credits = c.value; compute(); });
			t.addEventListener("change", () => { r.type = t.value; compute(); });
			const rm = btn("✕", () => { rows.splice(i, 1); draw(); compute(); }, "rv-fc-card-del");
			rm.setAttribute("aria-label", "Remove " + course);
			rowEl.append(name, g, c, t, rm);
			table.appendChild(rowEl);
		});
		compute();
	}
	panel.append(table, btn("+ Add course", () => { rows.push({ name: "", grade: "A", credits: "1", type: "reg" }); draw(); }, "rv-tbtn"), result);
	draw();
	panel.append(note("Uses the standard US 4.0 scale. Honors adds 0.5 and AP/IB adds 1.0 to non-failing grades - your school's exact weighting may differ."));
}
